import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Stethoscope, Building2, UserPlus } from "lucide-react";
import ProviderAccountDialog from "@/components/ProviderAccountDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { usePractice } from "@/pages/StaffApp";
import { byName } from "@/lib/status";

export function CheckList({ items, value, onChange, testid }) {
  const [q, setQ] = useState("");
  const shown = items.filter((x) => x.name.toLowerCase().includes(q.toLowerCase())).sort(byName);
  const all = shown.length > 0 && shown.every((x) => value.includes(x.code));
  return (
    <div data-testid={testid}>
      <div className="flex gap-2 items-center mb-2">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter" className="h-9 pl-8" data-testid={`${testid}-filter`} /></div>
        <Button size="sm" variant="outline" onClick={() => onChange(all ? value.filter((c) => !shown.some((x) => x.code === c)) : [...new Set([...value, ...shown.map((x) => x.code)])])} data-testid={`${testid}-all`}>{all ? "Clear shown" : "Tick all shown"}</Button>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-1.5 max-h-[50vh] overflow-y-auto pr-1">
        {shown.map((x) => (
          <label key={x.code} className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm cursor-pointer transition-colors ${value.includes(x.code) ? "bg-emerald-50 border-emerald-300" : "bg-white hover:bg-slate-50"}`}>
            <Checkbox checked={value.includes(x.code)} onCheckedChange={(v) => onChange(v ? [...value, x.code] : value.filter((c) => c !== x.code))} data-testid={`${testid}-${x.code}`} />
            <span className="truncate">{x.name}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function ProviderFacilities() {
  const { practice, reloadPractice, scope, can } = usePractice();
  const canAdd = can("member:manage");
  const [linked, setLinked] = useState(null);
  const [acct, setAcct] = useState(null);
  const loadLinked = () => api.get("/practice").then((r) => setLinked(new Set(r.data.members.map((m) => m.provider_code).filter(Boolean))));
  useEffect(() => { if (canAdd) { loadLinked(); } }, [canAdd]);
  const editable = (code) => !scope?.limited || scope.providers.includes(code);
  const providers = practice.providers.filter((p) => editable(p.code)).sort(byName);
  const [pq, setPq] = useState("");
  const shownProv = providers.filter((p) => `${p.name} ${p.code}`.toLowerCase().includes(pq.toLowerCase()));
  const [sel, setSel] = useState(providers[0]?.code);
  const prov = practice.providers.find((p) => p.code === sel);
  const [ticked, setTicked] = useState([]);
  useEffect(() => { setTicked(prov?.facility_codes || []); }, [prov]);
  const dirty = [...ticked].sort().join() !== [...(prov?.facility_codes || [])].sort().join();
  const pick = (code) => { if (dirty && !window.confirm("Discard unsaved facility changes?")) return; setSel(code); };
  const byFac = useMemo(() => Object.fromEntries(practice.facilities.map((f) => [f.code, practice.providers.filter((p) => (p.facility_codes || []).includes(f.code)).sort(byName)])), [practice]);
  const save = async () => {
    try { await api.put(`/practice/providers/${sel}/facilities`, { facility_codes: ticked }); toast.success("Facilities mapped (audited)"); reloadPractice(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="provider-facilities">
      <div className="grid lg:grid-cols-[280px_1fr] gap-4">
        <div className="bg-white border rounded-xl p-2 max-h-[60vh] overflow-y-auto">
          <p className="px-2 py-1 text-xs uppercase tracking-wider text-slate-500">Providers ({providers.length})</p>
          <div className="relative px-1 pb-1"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" /><Input value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Filter providers" className="h-9 pl-8" data-testid="pf-provider-filter" /></div>
          {shownProv.map((p) => (
            <div key={p.code} className={`flex items-center rounded-lg ${sel === p.code ? "bg-indigo-600 text-white" : "hover:bg-slate-50"}`}>
              <button onClick={() => pick(p.code)} data-testid={`pf-provider-${p.code}`} className="flex-1 min-w-0 text-left flex items-center gap-2 px-2.5 py-2 text-sm">
                <Stethoscope className="w-4 h-4 shrink-0" /><span className="flex-1 truncate">{p.name}</span>
                <span className={`text-xs rounded-full px-2 ${sel === p.code ? "bg-white/25" : "bg-slate-100"}`}>{(p.facility_codes || []).length}</span></button>
              {linked && !linked.has(p.code) && <button type="button" onClick={() => setAcct(p)} title="No staff account yet - create one" aria-label={`Create account for ${p.name}`} data-testid={`pf-create-account-${p.code}`}
                className={`mr-1.5 p-1 rounded-md transition-colors ${sel === p.code ? "text-amber-200 hover:bg-white/20" : "text-amber-600 hover:bg-amber-100"}`}><UserPlus className="w-4 h-4" /></button>}
            </div>))}
          {linked && <p className="px-2 pt-2 text-[11px] text-slate-500" data-testid="pf-no-account-count"><UserPlus className="w-3 h-3 inline mr-1 text-amber-600" />{providers.filter((p) => !linked.has(p.code)).length} without a staff account</p>}
        </div>
        {prov && (
          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3"><h3 className="font-semibold">Facilities for {prov.name}</h3><span className="text-xs text-slate-500">{ticked.length} ticked - this provider sees every patient at these facilities</span>
              <Button className="btn-brand ml-auto" size="sm" disabled={!dirty} onClick={save} data-testid="pf-save">{dirty ? "Save changes" : "Saved"}</Button></div>
            <CheckList items={practice.facilities} value={ticked} onChange={setTicked} testid="pf-facility" />
          </div>
        )}
      </div>
      <ProviderAccountDialog provider={acct} onClose={() => setAcct(null)} onAdded={loadLinked} />
      <div className="bg-white border rounded-xl" data-testid="pf-overview">
        <p className="px-4 py-3 border-b font-semibold flex items-center gap-2"><Building2 className="w-4 h-4 text-emerald-600" />All facilities and their providers</p>
        <div className="divide-y max-h-[50vh] overflow-y-auto">{[...practice.facilities].sort(byName).map((f) => (
          <div key={f.code} className="px-4 py-2 text-sm flex flex-wrap gap-2 items-center" data-testid={`pf-overview-${f.code}`}>
            <span className="font-medium w-64 truncate">{f.name}</span>
            {byFac[f.code].length ? byFac[f.code].map((p) => <span key={p.code} className="text-xs rounded-full bg-indigo-50 text-indigo-800 px-2 py-0.5">{p.name}</span>) : <span className="text-xs text-amber-700">No provider mapped</span>}
          </div>))}</div>
      </div>
    </div>
  );
}
